<script setup lang="ts">
import { AccountIcon, EmptyState } from '@taler/ui'
import { negateMoney } from '@taler/domain'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import type { AccountFlowRow } from '../internal/view-types.ts'
import { useI18n } from 'vue-i18n'

defineProps<{ rows: readonly AccountFlowRow[]; caption: string }>()
// The backend sends outflow and transfers out as magnitudes; they read as money leaving.
const outgoing = negateMoney
const { t } = useI18n()
</script>

<template>
  <EmptyState
    v-if="rows.length === 0"
    :title="t('reporting.empty.title')"
    :body="t('reporting.empty.body')"
  />
  <div
    v-else
    class="overflow-x-auto rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)]"
  >
    <table class="w-full border-collapse text-left text-[length:var(--text-step-2)]">
      <caption class="sr-only">
        {{
          caption
        }}
      </caption>
      <thead class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
        <tr>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] font-medium">
            {{ t('reporting.accounts.account') }}
          </th>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] text-right font-medium">
            {{ t('reporting.table.entries') }}
          </th>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] text-right font-medium">
            {{ t('reporting.accounts.inflow') }}
          </th>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] text-right font-medium">
            {{ t('reporting.accounts.outflow') }}
          </th>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] text-right font-medium">
            {{ t('reporting.accounts.transfersIn') }}
          </th>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] text-right font-medium">
            {{ t('reporting.accounts.transfersOut') }}
          </th>
          <th scope="col" class="px-[var(--space-group)] py-[var(--space-inline)] text-right font-medium">
            {{ t('reporting.accounts.net') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.key" class="border-t border-[color:var(--color-border)]">
          <th
            scope="row"
            class="px-[var(--space-group)] py-[var(--space-inline)] font-medium text-[color:var(--color-text)]"
          >
            <span class="flex items-center gap-2">
              <span aria-hidden="true" class="shrink-0"
                ><AccountIcon v-if="row.icon" :icon="row.icon" label=""
              /></span>
              {{ row.label }}
            </span>
          </th>
          <td
            class="px-[var(--space-group)] py-[var(--space-inline)] text-right tabular-nums text-[color:var(--color-text-muted)]"
          >
            {{ row.count }}
          </td>
          <td class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <MoneyAmount :value="row.inflow" sign tone="income" />
          </td>
          <td class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <MoneyAmount :value="outgoing(row.outflow)" sign tone="expense" />
          </td>
          <td class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <MoneyAmount :value="row.transfersIn" sign tone="transfer" />
          </td>
          <td class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <MoneyAmount :value="outgoing(row.transfersOut)" sign tone="transfer" />
          </td>
          <td class="px-[var(--space-group)] py-[var(--space-inline)] text-right">
            <MoneyAmount :value="row.net" sign />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
